import React, { useState, useEffect } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { Button, Modal, Form, Select, InputNumber, message, DatePicker, Col, Row, Divider } from 'antd';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { itemProducts } from '../../../shared';
import { createBulkCommodityRate } from '../../../RestServices/postingService';
import { getCommodity } from '../../../Redux/ActionCreaters/CommodityRequets';

const { Option } = Select;

const BulkRateEntry = ({ currentDate }) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const { allcities, provinces } = useSelector((state) => state.Cities);
  const { _id, token } = useSelector((state) => state.Authentication.user);
  
  // State for filtered cities
  const [filteredCities, setFilteredCities] = useState([]);
  const [citiesToBeCreated, setCitiesToBeCreated] = useState([]);
  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const [submitting, setSubmitting] = useState(false)

  // Filter cities whenever province or category changes
  useEffect(() => {
    if (selectedProvince && selectedCategory) {
      const filtered = allcities.filter(city => 
        city.province === selectedProvince && 
        city.activeForProduct && 
        city.activeForProduct.includes(selectedCategory)
      );
      setFilteredCities(filtered);
      
      // Clear cities selection when filters change
      form.setFieldsValue({ cities: [] });
    } else {
      setFilteredCities([]);
    }
  }, [selectedProvince, selectedCategory, allcities, form]);

  const dispatch = useDispatch()
  const handleSubmit = () => {
    form.validateFields().then(async values => {
      console.log('Form values:', values);
      setSubmitting(true)
      const selectedDate = form.getFieldValue('date') || moment();
      await createBulkCommodityRate({ 
        ...values, 
        cities: citiesToBeCreated,  
        userId: _id, 
        date: selectedDate.format("YYYY-MM-DD") 
      }, token)
      
      dispatch(getCommodity(values.category, values.province, token, selectedDate.format("YYYY-MM-DD")));
      
      // Here you would typically send this data to your API
      // For now, we'll just show a success message
      message.success('Rates saved successfully for all selected cities!');
      
      // Close modal and reset form
      setModalVisible(false);
      form.resetFields();
      form.setFieldsValue({ date: currentDate || moment() });
      setSelectedProvince(null);
      setSelectedCategory(null);
      setFilteredCities([]);

      
    }).catch(error => {
      console.error('Validation failed:', error);
    }).finally(() => {
      setSubmitting(false)
    });

    
  };

  const handleCancel = () => {
    setModalVisible(false);
    form.resetFields();
    form.setFieldsValue({ date: currentDate || moment() });
    setSelectedProvince(null);
    setSelectedCategory(null);
    setFilteredCities([]);
  };

  const handleProvinceChange = (value) => {
    setSelectedProvince(value);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  // Function to select all filtered cities
  const selectAllCities = () => {
    const allCityValues = filteredCities.map(city => city._id);
    form.setFieldsValue({ cities: allCityValues });
    setCitiesToBeCreated(filteredCities)
  };

  return (
    <div style={{ display: "flex" }}>
      <Button 
        style={{ marginRight: 10 }}
        type="primary" 
        icon={<PlusOutlined />}
        onClick={() => setModalVisible(true)}
        size="middle"
        className="responsive-btn"
      >
        Add Rates for Multiple Cities
      </Button>

      <Modal
        title="Add Rates for Multiple Cities"
        visible={modalVisible}
        onOk={handleSubmit}
        onCancel={handleCancel}
        width={600}
        confirmLoading={submitting}
        okText="Save All Rates"
        cancelText="Cancel"
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            announcedRate: 9060,
            actualPosition: 8915,
            date: currentDate || moment()
          }}
        >
          <Form.Item
            label="Select Date"
            name="date"
            rules={[{ required: true, message: 'Please select a date!' }]}
          >
            <DatePicker 
              style={{ width: '100%' }}
              size="large"
              format="YYYY-MM-DD"
            />
          </Form.Item>

          <Form.Item
            label="Select Province"
            name="province"
            rules={[{ required: true, message: 'Please select a province!' }]}
          >
            <Select
              placeholder="Select province"
              style={{ width: '100%' }}
              size="large"
              onChange={handleProvinceChange}
            >
              {provinces.map(province => (
                <Option key={province} value={province}>{province}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Please select a category!' }]}
          >
            <Select
              placeholder="Select category"
              size="large"
              style={{ width: '100%' }}
              onChange={handleCategoryChange}
            >
              {itemProducts.map(category => (
                <Option key={category} value={category}>{category}</Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={
              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                <span>Select Cities</span>
                {filteredCities.length > 0 && (
                  <Button 
                    type="link" 
                    size="small" 
                    onClick={selectAllCities}
                  >
                    Select All ({filteredCities.length})
                  </Button>
                )}
              </div>
            }
            name="cities"
            rules={[{ required: true, message: 'Please select at least one city!' }]}
          >
                  <Select
          mode="multiple"
          placeholder={
            !selectedProvince || !selectedCategory 
              ? "First select province and category" 
              : filteredCities.length === 0 
              ? "No cities available for selected filters"
              : "Select cities to update"
          }
          style={{ width: '100%' }}
          size="large"
          value={citiesToBeCreated.map(city => city._id)} // Use array of IDs instead of objects
          onChange={(selectedIds) => {
            // Convert selected IDs back to city objects
            const selectedCityObjects = filteredCities.filter(city => 
              selectedIds.includes(city._id)
            );
            //  form.setFieldsValue({ cities: selectedCityObjects });

            setCitiesToBeCreated(selectedCityObjects);
          }}
          disabled={!selectedProvince || !selectedCategory || filteredCities.length === 0}
          notFoundContent={
            !selectedProvince || !selectedCategory 
              ? "Select province and category first"
              : "No cities found for selected filters"
          }
        >
          {filteredCities?.map(city => (
            <Option key={city._id} value={city._id}> {/* Use ID as value */}
              {city.name} - {city.division}
            </Option>
          ))}
        </Select>
          </Form.Item>

          {filteredCities.length > 0 && (
            <div style={{ marginBottom: 16, color: '#1890ff' }}>
              Found {filteredCities.length} cities in {selectedProvince} for {selectedCategory}
            </div>
          )}

          <Divider />

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Announced Rate"
                name="announcedRate"
                rules={[{ required: true, message: 'Please enter announced rate!' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  size="large"
                  min={0}
                  placeholder="Enter announced rate"
                />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                label="Actual Position"
                name="actualPosition"
                rules={[{ required: false, message: 'Please enter actual position!' }]}
              >
                <InputNumber
                  style={{ width: '100%' }}
                  size="large"
                  min={0}
                  placeholder="Enter actual position"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label="Mandi Guru Rate"
                name="mandiGuruRate"
              >
                <InputNumber
                  style={{ width: '100%' }}
                  size="large"
                  min={0}
                  placeholder="Enter market rate (optional)"
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default BulkRateEntry;